/**
 * Kingdom Factor Collective - Article Page
 * - Full article content
 * - Author information
 * - Breadcrumb navigation
 * - Related articles
 */

import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { sanitizeContentHtml } from "@/lib/sanitize-html"
import { Clock, ArrowRight, Globe, Mail, Linkedin, Twitter, Facebook, Instagram, Printer, Youtube } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { ArticleCard, Breadcrumbs, SocialShare } from "./components"
import { normalizeExternalHref } from "@/lib/external-href"

export default function Show({
  seo,
  article = {},
  category = {},
  relatedArticles = [],
  settings = {},
}) {
  const { scheduleCallUrl = "#" } = settings

  const formattedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null

  // SEO: Meta description (used in SocialShare below)
  const metaDescription = article.metaDescription || article.excerpt || ""

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="collective" scheduleCallUrl={scheduleCallUrl} />

        {/* Article Section */}
        <section className="bg-zinc-50 pt-32 pb-16" data-print-root>
          {/* Breadcrumbs - full width */}
          <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-6 print:hidden">
            <Breadcrumbs
              items={[
                { label: "Collective", href: "/collective" },
                { label: category.name, href: `/collective/${category.slug}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Category badge & Collective link - full width */}
          <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-8 flex flex-wrap items-center gap-3 print:hidden">
            <Link
              href={`/collective/${category.slug}`}
              className="inline-block px-4 py-2 bg-kf-red text-white text-sm font-semibold uppercase tracking-wider hover:bg-kf-red/90 transition-colors"
            >
              {category.name}
            </Link>
            <Button variant="kf-outline" size="sm" asChild>
              <Link href="/collective">
                Explore The KF Collective
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

          {/* Article Card - narrower for readability */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <Card className="bg-white overflow-hidden">
              {/* Title & Meta */}
              <div className="px-6 lg:px-8 pt-8">
                <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-zinc-900 mb-6">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-zinc-500">
                  {article.author?.slug ? (
                    <Link
                      href={`/collective/coaches/${article.author.slug}`}
                      className="flex items-center gap-2 hover:text-zinc-900 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-full overflow-hidden bg-kf-gradient flex items-center justify-center flex-shrink-0">
                        {article.author?.headshotUrl ? (
                          <img
                            src={article.author.headshotUrl}
                            alt={article.author?.name || "Author"}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white text-xs font-display">
                            {article.author?.name?.charAt(0) || "K"}
                          </span>
                        )}
                      </div>
                      <span>{article.author?.name || "Kingdom Factor"}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full overflow-hidden bg-kf-gradient flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-display">
                          {article.author?.name?.charAt(0) || "K"}
                        </span>
                      </div>
                      <span>{article.author?.name || "Kingdom Factor"}</span>
                    </div>
                  )}
                  {formattedDate && (
                    <span>{formattedDate}</span>
                  )}
                  {article.readTime && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      <span>{article.readTime} min read</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Featured Image */}
              {article.featuredImageUrl && (
                <div className="px-6 lg:px-8 pt-8">
                  <div className="aspect-[2/1] rounded-lg overflow-hidden shadow-lg" data-print-figure>
                    <img
                      src={article.featuredImageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Podcast Player */}
              {article.podcastUrl && (
                <div className="px-6 lg:px-8 pt-8 print:hidden">
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-kf-red" />
                      Listen to this article
                    </p>
                    <PodcastEmbed url={article.podcastUrl} />
                  </div>
                </div>
              )}

              {/* Article Content */}
              <div className="px-6 lg:px-8 py-12">
                <div
                  data-print-body
                  className="prose prose-zinc prose-lg max-w-none
                    prose-headings:font-display prose-headings:text-zinc-900
                    prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                    prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                    prose-p:text-zinc-700 prose-p:leading-relaxed
                    prose-li:text-zinc-700
                    prose-a:text-kf-red prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-zinc-900"
                  dangerouslySetInnerHTML={{ __html: sanitizeContentHtml(article.content || "") }}
                />

                {/* Social Share + Print — the whole row is screen-only */}
                <div className="mt-12 pt-8 border-t border-zinc-200 flex flex-wrap items-center justify-between gap-4 print:hidden">
                  <SocialShare
                    url={article.canonicalUrl}
                    title={article.title}
                    description={metaDescription}
                  />
                  <Button variant="kf-outline" size="sm" onClick={() => window.print()}>
                    <Printer className="w-4 h-4" />
                    Print Article
                  </Button>
                </div>
              </div>

              {/* Author & Interviewee Section */}
              <div className="px-6 lg:px-8 pb-8 pt-6 border-t border-zinc-200">
                <div className={`grid grid-cols-1 ${(article.company || article.jobTitle) ? "md:grid-cols-2" : ""} gap-8 items-start`}>
                  {/* Author Column */}
                  <div className="text-center md:text-left">
                    <p className="text-sm text-zinc-500 uppercase tracking-wider mb-1">
                      Written by
                    </p>
                    <h3 className="font-display text-xl text-zinc-900 mb-2">
                      {article.author?.name || "Kingdom Factor"}
                    </h3>
                    <p className="text-zinc-600 mb-4">
                      {article.author?.bio || "Kingdom Factor Coach helping leaders integrate faith and business for lasting impact."}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center md:justify-start print:hidden">
                      {article.author?.slug && !article.author?.isGenericAccount && (
                        <Button variant="kf-outline" size="sm" asChild>
                          <Link href={`/collective/coaches/${article.author.slug}`}>
                            View All Articles
                          </Link>
                        </Button>
                      )}
                      {!article.author?.isGenericAccount && article.author?.scheduleCallUrl && article.author.scheduleCallUrl !== "#" && (
                        <Button variant="gradient" size="sm" asChild>
                          <a href={article.author.scheduleCallUrl} target="_blank" rel="noopener noreferrer">Schedule a Call</a>
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Interviewee Column */}
                  {(article.company || article.jobTitle) && (
                    <div className="text-center md:text-left md:border-l md:border-zinc-200 md:pl-8">
                      <p className="text-sm text-zinc-500 uppercase tracking-wider mb-1">
                        Interview with
                      </p>
                      {(article.intervieweeName || article.podcastIntervieweeName) && (
                        <h3 className="font-display text-xl text-zinc-900 mb-1">
                          {article.intervieweeName || article.podcastIntervieweeName}
                        </h3>
                      )}
                      <p className={`text-zinc-600 ${(article.city || article.state) ? "mb-1" : "mb-4"}`}>
                        {[article.jobTitle, article.company].filter(Boolean).join(" at ")}
                      </p>
                      {(article.city || article.state) && (
                        <p className="text-sm text-zinc-500 mb-4">
                          {[article.city, article.state, article.country && article.country !== "US" ? article.country : null].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {/* Social Links */}
                      {(article.intervieweeEmail || article.intervieweeWebsite || article.intervieweeLinkedin || article.intervieweeTwitter || article.intervieweeFacebook || article.intervieweeInstagram || article.intervieweeYoutube) && (
                        <div className="flex gap-2 justify-center md:justify-start">
                          {article.intervieweeEmail && (
                            <a href={`mailto:${article.intervieweeEmail}`} className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="Email">
                              <Mail className="w-5 h-5" />
                            </a>
                          )}
                          {article.intervieweeWebsite && (
                            <a href={normalizeExternalHref(article.intervieweeWebsite)} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="Website">
                              <Globe className="w-5 h-5" />
                            </a>
                          )}
                          {article.intervieweeLinkedin && (
                            <a href={normalizeExternalHref(article.intervieweeLinkedin)} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="LinkedIn">
                              <Linkedin className="w-5 h-5" />
                            </a>
                          )}
                          {article.intervieweeTwitter && (
                            <a href={normalizeExternalHref(article.intervieweeTwitter)} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="Twitter / X">
                              <Twitter className="w-5 h-5" />
                            </a>
                          )}
                          {article.intervieweeFacebook && (
                            <a href={normalizeExternalHref(article.intervieweeFacebook)} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="Facebook">
                              <Facebook className="w-5 h-5" />
                            </a>
                          )}
                          {article.intervieweeInstagram && (
                            <a href={normalizeExternalHref(article.intervieweeInstagram)} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="Instagram">
                              <Instagram className="w-5 h-5" />
                            </a>
                          )}
                          {article.intervieweeYoutube && (
                            <a href={normalizeExternalHref(article.intervieweeYoutube)} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-kf-red hover:text-white transition-colors" title="YouTube">
                              <Youtube className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-white py-16 print:hidden">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="font-display text-3xl text-zinc-900 mb-2">
                  Continue Reading
                </h2>
                <p className="text-zinc-600">
                  More articles in {category.name}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((relatedArticle) => (
                  <ArticleCard key={relatedArticle.id} article={relatedArticle} />
                ))}
              </div>

              <div className="text-center mt-10">
                <Link
                  href={`/collective/${category.slug}`}
                  className="inline-flex items-center gap-2 text-kf-red font-semibold uppercase tracking-wider text-sm hover:underline"
                >
                  View All {category.name} Articles
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="bg-kf-gradient py-16 print:hidden">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
              WANT TO SHARE YOUR STORY?
            </h2>
            <p className="text-white/80 mb-8">
              Join our community of faith-driven leaders and share how God is working in your business.
            </p>
            <Button variant="kf-secondary" asChild>
              <Link href="/share-your-story">Get Started</Link>
            </Button>
          </div>
        </section>

        <Footer activePage="collective" />
      </div>
    </>
  )
}

function PodcastEmbed({ url }) {
  if (!url) return null

  let embedId = url
  try {
    const urlObj = new URL(url)
    const iParam = urlObj.searchParams.get("i")
    if (iParam) embedId = iParam
  } catch {
    // url might just be the episode ID
  }

  const embedUrl = `https://www.podbean.com/player-v2/?i=${embedId}&share=1&download=1&fonts=Arial&skin=1&btn-skin=7&size=150`

  return (
    <iframe
      src={embedUrl}
      style={{ border: "none", minWidth: "min(100%,430px)", height: "150px" }}
      scrolling="no"
      allow="autoplay"
      title="Podcast player"
    />
  )
}
